"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProjectGallery() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const suites = [{
    number: "01",
    name: "Custom Estate",
    description: "Bespoke luxury home build featuring modern architecture and open floor plans.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/custom_home.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/custom_home.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/custom_home.webp-wvc-sizes",
    aspect: "aspect-[4/5]",
    colSpan: "lg:col-start-1 lg:col-end-6",
    offset: "lg:mt-0"
  }, {
    number: "02",
    name: "Modern Kitchen",
    description: "High-end kitchen renovation with custom cabinetry and premium stone surfaces.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/kitchen_remodel.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/kitchen_remodel.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/kitchen_remodel.webp-wvc-sizes",
    aspect: "aspect-[16/9]",
    colSpan: "lg:col-start-7 lg:col-end-13",
    offset: "lg:mt-20"
  }, {
    number: "03",
    name: "Master Bath",
    description: "Spa-inspired bathroom retreat with walk-in shower and soaking tub.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/bath_remodel.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/bath_remodel.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/bath_remodel.png-wvc-sizes",
    aspect: "aspect-[4/5]",
    colSpan: "lg:col-start-2 lg:col-end-7",
    offset: "lg:mt-32"
  }, {
    number: "04",
    name: "Home Addition",
    description: "Seamless expansion adding functional living space to your existing home.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/home_addition.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/home_addition.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/home_addition.png-wvc-sizes",
    aspect: "aspect-[16/9]",
    colSpan: "lg:col-start-8 lg:col-end-13",
    offset: "lg:mt-16"
  }, {
    number: "05",
    name: "Full Renovation",
    description: "Complete residential transformation focusing on quality and craftsmanship.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/full_renovation.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/full_renovation.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/full_renovation.webp-wvc-sizes",
    aspect: "aspect-[4/5]",
    colSpan: "lg:col-start-1 lg:col-end-6",
    offset: "lg:mt-28"
  }, {
    number: "06",
    name: "Outdoor Living",
    description: "Custom patio and outdoor kitchen design for Austin lifestyle enjoyment.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/outdoor_living.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/outdoor_living.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681426/img/outdoor_living.png-wvc-sizes",
    aspect: "aspect-[16/9]",
    colSpan: "lg:col-start-7 lg:col-end-13",
    offset: "lg:mt-20"
  }];
  return;
}
type Suite = {
  number: string;
  name: string;
  description: string;
  src: string;
  srcset: string;
  sizes: string;
  aspect: string;
  colSpan: string;
  offset: string;
};
function SuiteCard({
  suite,
  ...rest
}: {
  suite: Suite;
} & React.HTMLAttributes<HTMLDivElement>) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div {...rest} ref={ref} className={`${suite.colSpan} ${suite.offset} group cursor-none transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className={`relative overflow-hidden ${suite.aspect} bg-muted`}>
        <img src={suite.src} data-wvc-srcset={suite.srcset} data-wvc-sizes={suite.sizes} alt={suite.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" style={{
        filter: "saturate(0.95) contrast(1.02)"
      }} />

        <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-700 ease-out">
          <div className="bg-card text-card-foreground px-6 py-5 md:px-8 md:py-6 border-t border-border/40">
            <div className="flex items-baseline justify-between gap-4 mb-2">
              <h3 className="font-default text-xl md:text-2xl leading-tight text-card-foreground" style={{
              fontFamily: "var(--font-serif)"
            }}>
                {suite.name}
              </h3>
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground shrink-0" style={{
              fontFamily: "var(--font-mono)"
            }}>
                {suite.number}
              </span>
            </div>
            <p className="font-default text-sm leading-relaxed text-muted-foreground">
              {suite.description}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-baseline justify-between gap-4">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" style={{
        fontFamily: "var(--font-mono)"
      }}>
          {suite.number}
        </span>
        <span className="font-default text-sm italic text-foreground/70" style={{
        fontFamily: "var(--font-serif)"
      }}>
          {suite.name}
        </span>
      </div>
    </div>;
}